import React from 'react';

import { useNavigate } from 'react-router-dom';

import { Calendar, DollarSign, Edit, Plus, Trash2 } from 'lucide-react';

import {
	useDeleteRevenueConfigurationMutation,
	useGetRevenueConfigurationsQuery,
} from '../api';
import { useAuth } from '../providers/AppAuthProvider';

import { Badge, Button, PageContent, PageHeader, Typography } from './common';
import Loader from './Loader';

interface RevenueConfigListProps {
	onSelectConfig?: (configName: string) => void;
	onCreateNew?: () => void;
}

const RevenueConfigList: React.FC<RevenueConfigListProps> = ({
	onSelectConfig,
	onCreateNew,
}) => {
	const navigate = useNavigate();
	const { isAdmin } = useAuth();

	const handleSelectConfigInternal = (configId: string) => {
		if (onSelectConfig) {
			onSelectConfig(configId);
		} else {
			navigate(`/settings?tab=revenue&edit=${configId}`);
		}
	};

	const handleCreateNewInternal = () => {
		if (onCreateNew) {
			onCreateNew();
		} else {
			navigate('/settings?tab=revenue&create=true');
		}
	};

	const {
		data: configsResponse,
		isLoading,
		error,
		refetch,
	} = useGetRevenueConfigurationsQuery();

	const [deleteConfig, { isLoading: isDeleting }] =
		useDeleteRevenueConfigurationMutation();

	// Group configurations by name
	const configGroups = configsResponse?.data || {};

	// Convert to array for display - each entry is [name, configs[]]
	const configEntries = Object.entries(configGroups);

	const handleDelete = async (configId: string, configName: string) => {
		if (
			window.confirm(
				`Are you sure you want to delete the revenue configuration "${configName}"? This will delete all 3 risk tier configurations.`,
			)
		) {
			try {
				await deleteConfig(configId).unwrap();
				refetch();
			} catch (error) {
				console.error('Failed to delete config:', error);
				alert('Failed to delete configuration. Please try again.');
			}
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const formatPercent = (value: number) => {
		return `${value.toFixed(2)}%`;
	};

	const formatCurrency = (value: number) => {
		return `$${value.toFixed(2)}`;
	};

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center font-primary">
				<Loader size="xl" text="Loading revenue configurations..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center py-20 font-primary">
				<div className="text-center">
					<div className="text-error-600 text-lg mb-4 font-primary">
						Error loading revenue configurations
					</div>
					<p className="text-neutral-600 font-primary">
						Please try again later.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader
				title="Revenue Configurations"
				subtitle="Manage Schedule A buy rates and agent revenue share for profit analysis"
				variant="title"
				actions={
					<Button
						onClick={handleCreateNewInternal}
						disabled={!isAdmin}
						variant={isAdmin ? 'primary' : 'secondary'}
						icon={Plus}
						title={
							isAdmin
								? 'Create new revenue configuration'
								: 'Admin access required'
						}
					>
						Create New Configuration
					</Button>
				}
			/>

			<PageContent className="flex flex-1 flex-col max-w-[1280px] mx-auto">
				<div className="relative flex flex-1 flex-col">
					{/* Configurations List */}
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
						<div>
							<table className="w-[100%]">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Configuration Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Description
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Risk Tiers
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Agent Share
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Updated
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{configEntries
										.filter(([name, configs]) => configs && configs.length > 0)
										.map(([name, configs]) => {
											const firstConfig = configs[0];
											return (
												<tr
													key={firstConfig?.id || name}
													className="hover:bg-gray-50 cursor-pointer"
													onClick={() =>
														handleSelectConfigInternal(firstConfig?.id || name)
													}
												>
													<td className="px-6 py-4">
														<div className="flex items-center">
															<DollarSign className="w-4 h-4 mr-2 text-green-600" />
															<div className="text-sm font-medium text-gray-900">
																{name}
															</div>
														</div>
													</td>
													<td className="px-6 py-4">
														<div className="text-sm text-gray-900">
															{firstConfig?.description || 'No description'}
														</div>
													</td>
													<td className="px-6 py-4 whitespace-nowrap">
														<div className="flex items-center space-x-1">
															{configs.map((config) => (
																<Badge
																	key={config.risk_tier}
																	variant={
																		config.risk_tier === 'HIGH'
																			? 'error'
																			: config.risk_tier === 'MEDIUM'
																				? 'warning'
																				: 'success'
																	}
																>
																	{config.risk_tier}
																</Badge>
															))}
														</div>
													</td>
													<td className="px-6 py-4 whitespace-nowrap">
														<div className="text-sm font-medium text-gray-900">
															{formatPercent(
																firstConfig?.agent_revenue_share_percent || 0,
															)}
														</div>
													</td>
													<td className="px-6 py-4 whitespace-nowrap">
														<Badge
															variant={
																firstConfig?.is_active ? 'success' : 'gray'
															}
														>
															{firstConfig?.is_active ? 'Active' : 'Inactive'}
														</Badge>
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														<div className="flex items-center">
															<Calendar className="w-4 h-4 mr-1" />
															{firstConfig?.updated_at
																? formatDate(firstConfig.updated_at)
																: 'N/A'}
														</div>
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
														<div className="flex items-center space-x-2">
															<Button
																variant="outline"
																size="xs"
																onClick={(e) => {
																	e.stopPropagation();
																	handleSelectConfigInternal(
																		firstConfig?.id || name,
																	);
																}}
																disabled={!isAdmin}
																icon={Edit}
																title={
																	isAdmin
																		? 'Edit configuration'
																		: 'Admin access required'
																}
															>
																Edit
															</Button>
															<Button
																variant="outline"
																className="text-error-600 hover:text-error-700"
																size="xs"
																onClick={(e) => {
																	e.stopPropagation();
																	handleDelete(firstConfig?.id || name, name);
																}}
																disabled={isDeleting || !isAdmin}
																icon={Trash2}
																title={
																	isAdmin
																		? 'Delete configuration'
																		: 'Admin access required'
																}
															/>
														</div>
													</td>
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>

						{/* Empty State */}
						{configEntries.length === 0 && (
							<div className="text-center py-12 flex flex-col items-center justify-center">
								<div className="text-gray-400 mb-4">
									<DollarSign className="w-12 h-12 mx-auto" />
								</div>
								<Typography variant="h5" className="mb-2">
									No revenue configurations found
								</Typography>
								<Typography variant="body2" color="muted" className="mb-4">
									Get started by creating your first revenue configuration with
									Schedule A buy rates.
								</Typography>
								<Button
									onClick={handleCreateNewInternal}
									disabled={!isAdmin}
									variant={isAdmin ? 'primary' : 'secondary'}
									icon={Plus}
									title={
										isAdmin
											? 'Create first configuration'
											: 'Admin access required'
									}
								>
									Create First Configuration
								</Button>
							</div>
						)}
					</div>
				</div>
			</PageContent>
		</div>
	);
};

export default RevenueConfigList;
